"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Video, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConsentScreen } from "./consent-screen";
import { IdentityCapture } from "./identity-capture";
import { QuestionRunner } from "./question-runner";
import { useProctoring, useSnapshots, useLiveFrame } from "./hooks";
import {
  startInterview,
  finishInterview,
  type ClientQuestion,
} from "@/lib/interview/api";
import { useDict, useLocale } from "@/lib/i18n/context";
import { resolveApiError } from "@/lib/api-error";

type Phase =
  | "consent"
  | "requesting"
  | "preparing"
  | "identity"
  | "interview"
  | "finishing"
  | "finished"
  | "error";

const PAGE_BG =
  "min-h-screen bg-gradient-to-b from-brand-50 to-background px-4 py-8 dark:from-brand-950/60";

export function InterviewClient({
  token,
  candidateName,
  jobTitle,
  companyName,
  questionCount,
  maxDurationSec,
}: {
  token: string;
  candidateName: string;
  jobTitle?: string | null;
  companyName?: string | null;
  questionCount?: number;
  maxDurationSec?: number;
}) {
  const t = useDict();
  const { locale } = useLocale();
  const [phase, setPhase] = useState<Phase>("consent");
  const [error, setError] = useState<string>("");
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [questions, setQuestions] = useState<ClientQuestion[]>([]);
  const [deadlineMs, setDeadlineMs] = useState(0);
  const [finalStatus, setFinalStatus] = useState<string>("completed");
  const videoRef = useRef<HTMLVideoElement>(null);

  const proctorActive = phase === "identity" || phase === "interview";
  const { violations, flagged, warning, dismissWarning } = useProctoring(
    token,
    proctorActive,
    {
      message: t.client.voiceWarning,
      lang: locale === "ar" ? "ar-SA" : "en-US",
    },
    videoRef,
  );
  useSnapshots(token, videoRef, phase === "interview");
  useLiveFrame(token, videoRef, phase === "identity" || phase === "interview");

  // Keep the preview <video> bound to the stream across phase changes.
  useEffect(() => {
    if (stream && videoRef.current && phase === "interview") {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [stream, phase]);

  const begin = useCallback(async () => {
    setPhase("requesting");
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: true,
      });
      setStream(media);
      setPhase("preparing");
      const data = await startInterview(token);
      setQuestions(data.questions);
      setDeadlineMs(
        new Date(data.startedAt).getTime() + data.maxDurationSec * 1000,
      );
      setPhase("identity");
    } catch (err) {
      console.error(err);
      if (err instanceof DOMException) {
        setError(t.client.mediaError);
      } else {
        const msg = err instanceof Error ? err.message : "";
        setError(
          resolveApiError(t, { error: msg }, "interviewPrepFailed"),
        );
      }
      setPhase("error");
    }
  }, [token, t]);

  const finish = useCallback(async () => {
    setPhase("finishing");
    const res = await finishInterview(token);
    if (res?.status) setFinalStatus(res.status);
    stream?.getTracks().forEach((t) => t.stop());
    setPhase("finished");
  }, [token, stream]);

  // Stop tracks on unmount.
  useEffect(
    () => () => stream?.getTracks().forEach((t) => t.stop()),
    [stream],
  );

  return (
    <div className={PAGE_BG}>
      {/* Violation warning banner */}
      {warning && proctorActive && (
        <div className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-2 bg-red-600 px-4 py-2 text-sm font-medium text-white">
          <AlertTriangle className="h-4 w-4" />
          {t.client.warning} ({violations}).
          <button onClick={dismissWarning} className="ms-2 underline">
            {t.client.dismiss}
          </button>
        </div>
      )}

      <div className="mx-auto max-w-2xl">
        {phase === "consent" && (
          <ConsentScreen
            candidateName={candidateName}
            jobTitle={jobTitle}
            companyName={companyName}
            questionCount={questionCount}
            maxDurationSec={maxDurationSec}
            onAccept={begin}
          />
        )}

        {phase === "requesting" && (
          <Center>
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            <p className="mt-4 font-medium">{t.client.requesting}</p>
            <p className="text-sm text-muted">{t.client.allowAccess}</p>
          </Center>
        )}

        {phase === "preparing" && (
          <Center>
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            <p className="mt-4 font-medium">{t.client.preparing}</p>
            <p className="text-sm text-muted">{t.client.preparingHint}</p>
          </Center>
        )}

        {phase === "identity" && stream && (
          <IdentityCapture
            token={token}
            stream={stream}
            videoRef={videoRef}
            onDone={() => setPhase("interview")}
          />
        )}

        {phase === "interview" && (
          <div className="space-y-4">
            <QuestionRunner
              token={token}
              questions={questions}
              deadlineMs={deadlineMs}
              onFinish={finish}
            />
            {/* Persistent camera preview */}
            <div className="fixed bottom-4 end-4 z-40 w-40 overflow-hidden rounded-xl border-2 border-white shadow-lg sm:w-48">
              <div className="relative aspect-video bg-slate-900">
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="h-full w-full object-cover"
                />
                <div className="absolute start-1.5 top-1.5 flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-medium text-white">
                  <span className="recording-dot h-1.5 w-1.5 rounded-full bg-card" />
                  REC
                </div>
              </div>
            </div>
          </div>
        )}

        {phase === "finishing" && (
          <Center>
            <Loader2 className="h-8 w-8 animate-spin text-brand-600" />
            <p className="mt-4 font-medium">{t.client.submitting}</p>
            <p className="text-sm text-muted">{t.client.takesMoment}</p>
          </Center>
        )}

        {phase === "finished" && (
          <Center>
            {finalStatus === "violated" ? (
              <>
                <XCircle className="h-12 w-12 text-amber-500" />
                <h1 className="mt-4 text-2xl font-bold">{t.client.flaggedSubmitted}</h1>
                <p className="mt-1 text-muted">{t.client.flaggedMsg}</p>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                <h1 className="mt-4 text-2xl font-bold">{t.client.thankYou}</h1>
                <p className="mt-1 text-muted">{t.client.completeMsg}</p>
              </>
            )}
            {flagged && finalStatus !== "violated" && (
              <p className="mt-2 text-sm text-amber-600">
                {t.client.focusViolationsNote}
              </p>
            )}
          </Center>
        )}

        {phase === "error" && (
          <Center>
            <XCircle className="h-12 w-12 text-red-500" />
            <h1 className="mt-4 text-xl font-bold">{t.client.somethingWrong}</h1>
            <p className="mt-1 text-muted">{error}</p>
            <Button className="mt-6" onClick={() => location.reload()}>
              <Video className="h-4 w-4" /> {t.client.tryAgain}
            </Button>
          </Center>
        )}
      </div>
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <Card className="mx-auto max-w-md animate-fade-in">
      <CardContent className="flex flex-col items-center py-16 text-center">
        {children}
      </CardContent>
    </Card>
  );
}
